import { Flame, Heart, Pin, Repeat2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import type { Post, PostContext } from '@/api/types'
import { isDeleted } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { LockBadge } from '@/components/ui/LockBadge'
import { cn } from '@/lib/cn'
import { useI18n, useT, useTx } from '@/i18n'
import { useFormat } from '@/lib/format'
import { renderContent } from '@/lib/text'

import { IdentityBadge } from '@/features/identity'

import { LinkPreviewCard } from './LinkPreviewCard'
import { MediaGrid } from './MediaGrid'
import { PostActions } from './PostActions'
import { PollCard } from './PollCard'
import { PostMenu } from './PostMenu'
import { QuoteCard } from './QuoteCard'

interface Props {
  post: Post
  /** 线程里连接上下帖子的竖线 */
  connector?: 'none' | 'below' | 'above' | 'both'
  onRequireLogin?: () => void
  /** 用户页顶上的置顶帖：头部多一行「已置顶」 */
  pinned?: boolean
}

/**
 * 时间线里的一条帖子。
 * 转发行的 author 是转发者，内容取 repost_of；点卡片进详情，
 * 内部链接与操作按钮不冒泡。
 */
export function PostCard({ post, connector = 'none', onRequireLogin, pinned = false }: Props) {
  const navigate = useNavigate()
  const t = useT()
  const tx = useTx()
  const { relativeTime } = useFormat()

  // 转发行：头部显示"X 转发了"，主体渲染原帖
  const repostOf = post.repost_of
  const isRepostRow = Boolean(repostOf)
  const display = repostOf && !isDeleted(repostOf) ? repostOf : post
  const repostedBy = isRepostRow ? post.author : null

  if (repostOf && isDeleted(repostOf)) {
    return (
      <article className="border-b border-line px-4 py-3 text-[15px] text-ink-muted">
        {repostOf.protected
          ? t('protected.reposted', { name: post.author.display_name })
          : t('post.repostedDeleted', { name: post.author.display_name })}
      </article>
    )
  }

  const permalink = `/${display.author.username}/status/${display.id}`
  const viewer = display.viewer

  const openDetail = () => navigate(permalink)

  return (
    <article
      onClick={openDetail}
      className="relative cursor-pointer border-b border-line px-4 py-3 transition-colors hover:bg-surface-hover"
    >
      {pinned && (
        <div className="mb-1 flex items-center gap-2 pl-[3.25rem] text-[13px] font-bold text-ink-muted">
          <Pin className="size-4" />
          {t('post.pinned')}
        </div>
      )}
      {repostedBy && (
        <div className="mb-1 flex items-center gap-2 pl-[3.25rem] text-[13px] font-bold text-ink-muted">
          <Repeat2 className="size-4" />
          <Link
            to={`/${repostedBy.username}`}
            onClick={(event) => event.stopPropagation()}
            className="hover:underline"
          >
            {t('post.repostedBy', { name: repostedBy.display_name })}
          </Link>
        </div>
      )}

      {post.context && <ContextLine context={post.context} />}

      <div className="flex gap-3">
        <div className="relative flex shrink-0 flex-col items-center">
          {(connector === 'above' || connector === 'both') && (
            <span className="absolute -top-3 h-3 w-0.5 bg-line-strong" aria-hidden />
          )}
          <Avatar
            src={display.author.avatar_url}
            alt={display.author.display_name}
            to={`/${display.author.username}`}
          />
          {(connector === 'below' || connector === 'both') && (
            <span className="mt-1 w-0.5 flex-1 bg-line-strong" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <header className="flex items-start gap-1 text-[15px]">
            <Link
              to={`/${display.author.username}`}
              onClick={(event) => event.stopPropagation()}
              className="truncate font-bold hover:underline"
            >
              {display.author.display_name}
            </Link>
            {display.author.is_private && <LockBadge className="mt-1" />}
            <span className="truncate font-mono text-[14px] text-ink-muted">
              @{display.author.username}
            </span>
            <IdentityBadge
              authorKind={display.author_kind}
              attestation={display.attestation}
              author={display.author}
            />
            <span className="text-ink-muted">·</span>
            <Link
              to={permalink}
              onClick={(event) => event.stopPropagation()}
              className="shrink-0 font-mono text-[14px] text-ink-muted hover:underline"
            >
              {relativeTime(display.created_at)}
            </Link>

            {viewer && (
              <div className="ml-auto">
                {/* 自己的帖子给置顶 / 回复设置 / 删除，别人的给静音 / 屏蔽 / 举报 */}
                <PostMenu post={display} />
              </div>
            )}
          </header>

          {display.reply_to && !display.reply_to.deleted && display.reply_to.author_username && (
            <p className="text-[15px] text-ink-muted">
              {tx('post.replyingToUser', {
                user: (
                  <Link
                    to={`/${display.reply_to.author_username}`}
                    onClick={(event) => event.stopPropagation()}
                    className="text-brand-ink hover:underline"
                  >
                    @{display.reply_to.author_username}
                  </Link>
                ),
              })}
            </p>
          )}

          {display.prompted_by?.username && (
            <p className="text-[13px] text-ink-muted">
              {tx('post.claimedBy', {
                user: (
                  <Link
                    to={`/${display.prompted_by.username}`}
                    onClick={(event) => event.stopPropagation()}
                    className="text-brand-ink hover:underline"
                  >
                    @{display.prompted_by.username}
                  </Link>
                ),
              })}
            </p>
          )}

          {display.content && (
            <p className={cn('post-text text-[15px] leading-normal')}>
              {renderContent(display.content, display.entities)}
            </p>
          )}

          {display.poll && <PollCard post={display} onRequireLogin={onRequireLogin} />}
          {display.media.length > 0 && <MediaGrid media={display.media} />}
          {/* 图片、引用卡、链接卡只取其一，三张卡片叠在一起太吵 */}
          {display.media.length === 0 && !display.quote_of && display.link_preview && (
            <LinkPreviewCard preview={display.link_preview} />
          )}
          {display.quote_of && <QuoteCard post={display.quote_of} />}

          <PostActions post={display} onRequireLogin={onRequireLogin} />
        </div>
      </div>
    </article>
  )
}

/** 为你推荐里写明为什么给你看这条（D83）。样式和「X 转发了」一致 */
function ContextLine({ context }: { context: PostContext }) {
  const { t, tp } = useI18n()
  if (context.kind === 'popular') {
    return (
      <div className="mb-1 flex items-center gap-2 pl-[3.25rem] text-[13px] font-bold text-ink-muted">
        <Flame className="size-4" />
        {t('post.popular')}
      </div>
    )
  }
  const name = context.user.display_name
  return (
    <div className="mb-1 flex items-center gap-2 pl-[3.25rem] text-[13px] font-bold text-ink-muted">
      <Heart className="size-4" />
      <Link
        to={`/${context.user.username}`}
        onClick={(event) => event.stopPropagation()}
        className="hover:underline"
      >
        {context.others > 0
          ? tp('post.likedByOthers', context.others, { name })
          : t('post.likedBy', { name })}
      </Link>
    </div>
  )
}
